import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
	FaSearch,
	FaTimes,
	FaFilter,
} from "react-icons/fa";
import { IoCart, IoHomeOutline, IoHeartOutline, IoGridOutline, IoPersonOutline } from "react-icons/io5";

const PriceFilterOverlay = ({ isOpen, onClose, onFilter, initialMin, initialMax }) => {
	const [localMin, setLocalMin] = useState(initialMin || "");
	const [localMax, setLocalMax] = useState(initialMax || "");
	useEffect(() => {
		setLocalMin(initialMin || "");
		setLocalMax(initialMax || "");
	}, [isOpen, initialMin, initialMax]);
	// Fallback to no-op if onFilter is not provided
	const safeOnFilter = typeof onFilter === 'function' ? onFilter : () => {};
	if (!isOpen) return null;
	return (
		<div
			className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
			onClick={onClose}
		>
			<div
				className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white p-4 shadow-2xl"
				onClick={(event) => event.stopPropagation()}
			>
				<div className="flex items-center justify-between mb-2">
					<h3 className="text-lg font-semibold">Filter by price</h3>
					<button onClick={onClose} className="p-1 -mr-1">
						<FaTimes className="h-5 w-5" />
					</button>
				</div>
				<p className="text-gray-600 mb-6">Input your price budget</p>
				<form
					onSubmit={e => {
						e.preventDefault();
						safeOnFilter(localMin, localMax);
					}}
					className="space-y-4"
				>
					<div>
						<label className="block text-sm mb-1.5">Minimum budget</label>
						<input
							type="number"
							placeholder="Minimum budget"
							value={localMin}
							onChange={e => setLocalMin(e.target.value)}
							className="w-full px-4 py-3 rounded-lg border border-gray-700 outline-none focus:ring-2 focus:ring-[#19C463] focus:border-[#19C463] transition"
						/>
					</div>
					<div>
						<label className="block text-sm mb-1.5">Maximum budget</label>
						<input
							type="number"
							placeholder="Maximum budget"
							value={localMax}
							onChange={e => setLocalMax(e.target.value)}
							className="w-full px-4 py-3 rounded-lg border border-gray-700 outline-none focus:ring-2 focus:ring-[#19C463] focus:border-[#19C463] transition"
						/>
					</div>
					<button
						type="submit"
						className="w-full bg-[#19C463] hover:bg-[#13a553] active:bg-[#0f8b45] transition text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2 mt-2"
					>
						Filter <FaFilter className="h-4 w-4" />
					</button>
				</form>
			</div>
		</div>
	);
};


const SearchResultsScreen = () => {
	const location = useLocation();
	const navigate = require("react-router-dom").useNavigate();
	const [searchResults, setSearchResults] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [minPrice, setMinPrice] = useState("");
	const [maxPrice, setMaxPrice] = useState("");
	const filters = ["All", "Price", "Size"];
	const [activeFilter, setActiveFilter] = useState("All");
	const [isPriceFilterOpen, setIsPriceFilterOpen] = useState(false);
	const [cartCounts, setCartCounts] = useState({});
	const [cartMessage, setCartMessage] = useState("");
	const [searchActive, setSearchActive] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [submittedSearch, setSubmittedSearch] = useState("");
	// Get search term and price filters from URL
	const searchTerm = new URLSearchParams(location.search).get("q") || "";
	useEffect(() => {
		setSearchValue(searchTerm);
		setSubmittedSearch(searchTerm); // initialize submittedSearch from URL
		const params = new URLSearchParams(location.search);
		setMinPrice(params.get("min") || "");
		setMaxPrice(params.get("max") || "");
	}, [location.search, searchTerm]);
	// Fetch products when submittedSearch, minPrice, or maxPrice changes
	useEffect(() => {
		if (!submittedSearch) return;
		setLoading(true);
		setError(null);
		let url = `${process.env.REACT_APP_API_URL}/api/products/search?q=${encodeURIComponent(submittedSearch)}`;
		if (minPrice) url += `&min=${encodeURIComponent(minPrice)}`;
		if (maxPrice) url += `&max=${encodeURIComponent(maxPrice)}`;
		fetch(url)
			.then(res => res.json())
			.then(result => {
				if (result.success && Array.isArray(result.data)) {
					setSearchResults(result.data);
				} else {
					setSearchResults([]);
					setError("No products found.");
				}
				setLoading(false);
			})
			.catch(() => {
				setSearchResults([]);
				setError("Error fetching products.");
				setLoading(false);
			});
	}, [submittedSearch, minPrice, maxPrice]);
	// Fetch cart from backend on mount
	useEffect(() => {
		const fetchCart = async () => {
			try {
				const token = localStorage.getItem("userToken");
				const headers = { "Content-Type": "application/json" };
				if (token) headers["Authorization"] = `Bearer ${token}`;
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart`, {
					credentials: "include",
					headers,
				});
				if (response.status === 200) {
					const data = await response.json();
					const counts = {};
					if (data && data.data && Array.isArray(data.data.items)) {
						data.data.items.forEach(item => {
							counts[item.productId] = item.quantity;
						});
					}
					setCartCounts(counts);
				} else {
					setCartCounts({});
				}
			} catch (err) {
				setCartCounts({});
			}
		};
		fetchCart();
	}, []);
	// Add to cart API logic
	const handleAdd = async (id) => {
		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart/add`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ productId: id, quantity: 1 }),
				credentials: "include"
			});
			const data = await response.json();
			if (data.success) {
				setCartMessage(data.message);
				setTimeout(() => setCartMessage(""), 2000);
				setCartCounts((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
			} else {
				setCartMessage(data.message || "Failed to add product to cart.");
				setTimeout(() => setCartMessage(""), 2000);
			}
		} catch (err) {
			setCartMessage("Failed to add product to cart.");
			setTimeout(() => setCartMessage(""), 2000);
		}
	};
	// Remove from cart logic
	const handleRemove = (id) => {
		setCartCounts((prev) => {
			const newCounts = { ...prev };
			if (newCounts[id] > 1) {
				newCounts[id] = newCounts[id] - 1;
			} else {
				delete newCounts[id];
			}
			return newCounts;
		});
	};
	// Handler for filter submit
	const handlePriceFilter = (newMin, newMax) => {
		setIsPriceFilterOpen(false);
		// Update URL params
		const params = new URLSearchParams(location.search);
		if (newMin) {
			params.set("min", newMin);
		} else {
			params.delete("min");
		}
		if (newMax) {
			params.set("max", newMax);
		} else {
			params.delete("max");
		}
		navigate({ pathname: location.pathname, search: params.toString() }, { replace: false });
		setMinPrice(newMin);
		setMaxPrice(newMax);
		// useEffect will refetch with new minPrice/maxPrice
	};

	function handleSearchSubmit() {
		if (!searchValue.trim()) return;
		setSubmittedSearch(searchValue.trim());
		const params = new URLSearchParams(location.search);
		params.set("q", searchValue.trim());
		navigate({ pathname: location.pathname, search: params.toString() }, { replace: false });
	}

	return (
		<>
			<div className="min-h-screen bg-[#F1F3F2] text-gray-900">
				<header className="sticky top-0 z-30 bg-white mb-3">
					<div className="px-4 pt-4 pb-4">
						<div className="flex items-center gap-3">
							<img
								src="/BUY%20iT%20Logo.png"
								alt="BUY iT logo"
								className="h-10 w-10 object-contain"
							/>
							<div className="flex-1">
								   {!searchActive ? (
									<div className="relative">
										<input
											type="text"
											placeholder="Search products"
											className="w-full bg-white py-2 text-sm focus:outline-none transition-all duration-300"
											style={{ borderRadius: "8px", paddingLeft: "12px", paddingRight: "8px", border: "2px solid #000000", width: "100%" }}
											value={searchValue}
											onFocus={() => setSearchActive(true)}
											onChange={e => setSearchValue(e.target.value)}
											onKeyDown={e => {
												if (e.key === 'Enter') {
													handleSearchSubmit();
												}
											}}
										/>
										<FaSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer" onClick={handleSearchSubmit} />
									</div>
								) : (
									<div className="flex items-center gap-2 bg-white py-2 px-2 rounded-lg shadow-sm transition-all duration-300" style={{ width: "100%", border: "2px solid #000000" }}>
										<FaTimes className="h-5 w-5 text-gray-700 cursor-pointer" onClick={() => { setSearchActive(false); setSearchValue(searchValue); }} />
										<input
											type="text"
											value={searchValue}
											onChange={e => setSearchValue(e.target.value)}
											placeholder="Search products"
											className="flex-1 bg-white text-sm focus:outline-none"
											style={{ border: "none", outline: "none", paddingLeft: "8px" }}
											autoFocus
											onKeyDown={e => {
												if (e.key === 'Enter') {
													handleSearchSubmit();
												}
											}}
										/>
										<FaSearch className="h-5 w-5 text-gray-700 cursor-pointer" onClick={handleSearchSubmit} />
									</div>
								)}
							</div>
							<div className="relative">
								{/* Show total quantity of all products in cart, only if cart is not empty */}
								{Object.keys(cartCounts).length > 0 && (
									<span className="absolute -right-1 -top-1 h-5 min-w-[20px] rounded-full bg-[#19C463] px-1 text-xs text-white flex items-center justify-center">
										{Object.keys(cartCounts).length}
									</span>
								)}
								<button
									type="button"
									className="h-10 w-10 bg-white flex items-center justify-center"
									aria-label="Cart"
									style={{ borderRadius: "4px", boxShadow: "0 0 4px rgba(0, 0, 0, 0.25)" }}
									onClick={() => navigate('/cart')}
								>
									<svg
										viewBox="0 0 24 24"
										className="h-5 w-5 text-gray-700"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<circle cx="9" cy="21" r="1" />
										<circle cx="20" cy="21" r="1" />
										<path d="M1 1h4l2.5 13h11.5l2-8H6" />
									</svg>
								</button>
							</div>
						</div>
					</div>
				</header>
				<div className="flex items-center gap-2 pb-4">
					<FaSearch className="text-gray-500" />
					<span className="text-sm font-medium text-gray-700">{searchValue}</span>
				</div>
				<div className="flex gap-3 pl-2 py-2 pb-3">
					{filters.map((filter) => {
						const isActive = activeFilter === filter;
						return (
							<button
								key={filter}
								type="button"
								onClick={() => {
									setActiveFilter(filter);
									if (filter === "Price") {
										setIsPriceFilterOpen(true);
									}
								}}
								className={`text-sm font-medium transition ${isActive ? "bg-[#A3E7C1]" : "bg-white"} px-3 py-2 rounded-lg border border-gray-200 shadow-sm`}
							>
								{filter}
							</button>
						);
					})}
				</div>
				{/* Render search results */}
				{cartMessage && (
					<div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-[#19C463] text-white px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut" style={{ minWidth: '220px', textAlign: 'center', letterSpacing: '0.5px' }}>
						{cartMessage}
					</div>
				)}
				{loading ? (
					<div className="col-span-2 text-center py-8 text-gray-500">Loading products...</div>
				) : error ? (
					<div className="col-span-2 text-center py-8 text-red-500">{error}</div>
				) : !Array.isArray(searchResults) || searchResults.length === 0 ? (
					<div className="col-span-2 text-center py-8 text-gray-500">No products found.</div>
				) : (
					<div className="bg-white pb-24">
						<div className="grid grid-cols-2 gap-x-[2px] gap-y-[6px]">
							{(searchResults || []).map((product) => {
								const count = cartCounts[product._id] || 0;
								return (
									<div
										key={product._id}
										className="rounded-lg p-1 flex flex-col gap-1"
									>
										<div className="rounded-t-[4px] aspect-[10/9] w-full overflow-hidden cursor-pointer"
											onClick={() => navigate(`/product/${product._id}`)}>
											<img
												loading="lazy"
												src={product.productImages && product.productImages[0] ? product.productImages[0] : '/placeholder.png'}
												alt={product.productName}
												className="h-full w-full object-cover"
											/>
										</div>
										<div className="mt-3 text-left cursor-pointer"
											onClick={() => navigate(`/product/${product._id}`)}>
											<p className="text-sm font-medium text-gray-900 truncate">
												{product.productName} - {product.productDescription}
											</p>
											{product.productQuantity <= 5 && (
												<span className="mt-1 inline-block text-xs font-bold text-[#19C463] tracking-wider" style={{ textTransform: 'uppercase' }}>
													ONLY 1 LEFT
												</span>
											)}
											<div className="mt-2 flex items-center gap-2">
												<span className="text-sm font-semibold text-gray-900">
													₦ {Number(product.productSellingPrice || 0).toLocaleString("en-NG")}
												</span>
												<span className="text-xs text-gray-500">{product.productSold}</span>
											</div>
										</div>
										<div className="mt-3">
											{count > 0 ? (
												<div className="flex items-center justify-between px-2 py-1">
													<button
														type="button"
														onClick={() => handleRemove(product._id)}
														className="h-8 w-8 bg-[#19C463] text-white"
														aria-label="Decrease quantity"
														style={{ borderRadius: "4px" }}
													>
														-
													</button>
													<span className="text-sm font-bold text-black">{count}</span>
													<button
														type="button"
														onClick={() => handleAdd(product._id)}
														className="h-8 w-8 bg-[#19C463] text-white"
														aria-label="Increase quantity"
														style={{ borderRadius: "4px" }}
													>
														+
													</button>
												</div>
											) : (
												<button
													type="button"
													onClick={() => handleAdd(product._id)}
													className="w-full flex items-center justify-center gap-2 rounded bg-[#19C463] px-1 py-2.5 text-sm font-medium text-white transition"
													style={{ borderRadius: "4px" }}
												>
													Add to cart
													<IoCart className="h-4 w-4" />
												</button>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}
				<nav className="fixed bottom-0 inset-x-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.08)]">
					<div className="flex items-center justify-around py-3">
						<button
							type="button"
							onClick={() => navigate("/home")}
							className="flex flex-col items-center gap-1"
							style={{ color: location.pathname === "/home" ? "#19C463" : "#000000" }}
						>
							<IoHomeOutline className="h-6 w-6" />
							<span className="p-1 text-sm">Home</span>
						</button>
						<button type="button" className="flex flex-col items-center gap-1" style={{ color: "#000000" }}>
							<IoHeartOutline className="h-6 w-6" />
							<span className="p-1 text-sm">Wishlist</span>
						</button>
						<button
							type="button"
							onClick={() => navigate("/category")}
							className="flex flex-col items-center gap-1"
							style={{ color: location.pathname === "/category" ? "#19C463" : "#000000" }}
						>
							<IoGridOutline className="h-6 w-6" />
							<span className="p-1 text-sm">Category</span>
						</button>
						<button
							type="button"
							onClick={() => navigate("/profile")}
							className="flex flex-col items-center gap-1"
							style={{ color: location.pathname === "/profile" ? "#19C463" : "#000000" }}
						>
							<IoPersonOutline className="h-6 w-6" />
							<span className="p-1 text-sm">Profile</span>
						</button>
					</div>
				</nav>
				<PriceFilterOverlay
					isOpen={isPriceFilterOpen}
					onClose={() => setIsPriceFilterOpen(false)}
					onFilter={handlePriceFilter}
					initialMin={minPrice}
					initialMax={maxPrice}
				/>
			</div>
		</>
	);
};

export default SearchResultsScreen;


